package com.rosario.juridico.ui.screens

import com.rosario.juridico.ui.components.CajaResultadoAccionable
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import com.rosario.juridico.domain.*
import com.rosario.juridico.navigation.Routes
import com.rosario.juridico.ui.components.JuridicoDatePickerDialogKMP
import juridico5.composeapp.generated.resources.Res
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.minus
import kotlinx.datetime.plus
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext
import org.jetbrains.compose.resources.ExperimentalResourceApi

private enum class PlazoUnidad {
    MESES,
    DIAS,
}

private enum class SeccionForm {
    LOCADOR,
    LOCATARIO,
    INMUEBLE,
    CONDICIONES,
    PRECIO,
    TASAS_SERVICIOS,
    INVENTARIO,
    GARANTIAS,
    FECHA_FIRMA,
}

private enum class CategoriaInventario(val label: String) {
    MUEBLE("Mueble"),
    ELECTRODOMESTICO("Electrodomestico"),
    UTIL("Util o herramienta"),
    LUMINARIA("Luminaria"),
    OTRO("Otro"),
}

private data class ModeloContrato(
    val clave: String,
    val titulo: String,
    val plazoUnidad: PlazoUnidad = PlazoUnidad.MESES,
    val secciones: Set<SeccionForm> = SeccionForm.entries.toSet(),
    val plazoDefault: String = "",
    val monedaDolar: Boolean = false,
    val esTemporario: Boolean = false,
    val labelLocador: String = "Locador",
    val labelLocatario: String = "Locatario",
)

private data class TasaServicio(
    val tipo: String = "",
    val numeroCuenta: String = "",
)

private data class ItemInventario(
    val descripcion: String = "",
    val categoria: CategoriaInventario = CategoriaInventario.MUEBLE,
    val cantidad: String = "1",
    val detalles: String = "",
)

private data class GarantePropietario(
    val nombre: String = "",
    val dni: String = "",
    val domicilio: String = "",
    val email: String = "",
    val telefono: String = "",
    val direccionInmueble: String = "",
    val datosInmueble: String = "",
)

private data class GaranteReciboSueldo(
    val nombre: String = "",
    val dni: String = "",
    val domicilio: String = "",
    val email: String = "",
    val telefono: String = "",
    val empleador: String = "",
    val domicilioEmpleador: String = "",
    val emailEmpleador: String = "",
    val telefonoEmpleador: String = "",
    val montoRecibo: String = "",
    val nombreFiador: String = "",
    val dniFiador: String = "",
    val domicilioFiador: String = "",
    val emailFiador: String = "",
    val telefonoFiador: String = "",
)

private data class GarantiaCaucion(
    val aseguradora: String = "",
    val numeroPoliza: String = "",
    val domicilio: String = "",
    val email: String = "",
    val telefono: String = "",
)

private val modelosContrato = listOf(
    ModeloContrato("contrato_locacion_familiar", "Locacion familiar", plazoDefault = "36"),
    ModeloContrato(
        "renovacion_contrato_vivienda",
        "Renovacion vivienda",
        secciones = setOf(SeccionForm.LOCADOR, SeccionForm.LOCATARIO, SeccionForm.INMUEBLE, SeccionForm.PRECIO, SeccionForm.FECHA_FIRMA),
    ),
    ModeloContrato(
        "prorroga_contrato_vivienda",
        "Prorroga vivienda",
        secciones = setOf(SeccionForm.LOCADOR, SeccionForm.LOCATARIO, SeccionForm.INMUEBLE, SeccionForm.PRECIO, SeccionForm.FECHA_FIRMA),
    ),
    ModeloContrato("alquiler_local_comercial", "Locacion comercial", plazoDefault = "36"),
    ModeloContrato(
        "contrato_para_oficina",
        "Contrato oficina",
        secciones = setOf(SeccionForm.LOCADOR, SeccionForm.LOCATARIO, SeccionForm.INMUEBLE, SeccionForm.PRECIO, SeccionForm.FECHA_FIRMA),
    ),
    ModeloContrato(
        "alquiler_galpon_deposito",
        "Alquiler galpon",
        secciones = setOf(SeccionForm.LOCADOR, SeccionForm.LOCATARIO, SeccionForm.INMUEBLE, SeccionForm.PRECIO, SeccionForm.FECHA_FIRMA),
    ),
    ModeloContrato(
        "alquiler_consultorio",
        "Alquiler consultorio",
        secciones = setOf(SeccionForm.LOCADOR, SeccionForm.LOCATARIO, SeccionForm.INMUEBLE, SeccionForm.PRECIO, SeccionForm.FECHA_FIRMA),
    ),
    ModeloContrato(
        "alquiler_temporario_turistico",
        "Temporario turistico",
        plazoUnidad = PlazoUnidad.DIAS,
        esTemporario = true,
        secciones = setOf(SeccionForm.LOCADOR, SeccionForm.LOCATARIO, SeccionForm.INMUEBLE, SeccionForm.PRECIO, SeccionForm.FECHA_FIRMA),
    ),
    ModeloContrato(
        "alquiler_temporario_laboral",
        "Temporario laboral",
        secciones = setOf(SeccionForm.LOCADOR, SeccionForm.LOCATARIO, SeccionForm.INMUEBLE, SeccionForm.PRECIO, SeccionForm.FECHA_FIRMA),
        esTemporario = true,
    ),
    ModeloContrato(
        "alquiler_por_temporada",
        "Alquiler temporada",
        plazoUnidad = PlazoUnidad.DIAS,
        esTemporario = true,
        secciones = setOf(SeccionForm.LOCADOR, SeccionForm.LOCATARIO, SeccionForm.INMUEBLE, SeccionForm.PRECIO, SeccionForm.FECHA_FIRMA),
    ),
    ModeloContrato(
        "leasing_inmobiliario",
        "Leasing inmobiliario",
        secciones = setOf(SeccionForm.LOCADOR, SeccionForm.LOCATARIO, SeccionForm.INMUEBLE, SeccionForm.PRECIO, SeccionForm.FECHA_FIRMA),
    ),
    ModeloContrato(
        "comodato_prestamo_gratuito",
        "Comodato",
        labelLocador = "Comodante",
        labelLocatario = "Comodatario",
        secciones = setOf(SeccionForm.LOCADOR, SeccionForm.LOCATARIO, SeccionForm.INMUEBLE, SeccionForm.FECHA_FIRMA),
    ),
    ModeloContrato(
        "alquiler_opcion_a_compra",
        "Alquiler with opcion a compra",
        secciones = setOf(SeccionForm.LOCADOR, SeccionForm.LOCATARIO, SeccionForm.INMUEBLE, SeccionForm.PRECIO, SeccionForm.FECHA_FIRMA),
    ),
    ModeloContrato(
        "contrato_sublocacion",
        "Sublocacion",
        labelLocador = "Sublocador",
        labelLocatario = "Sublocatario",
        secciones = setOf(SeccionForm.LOCADOR, SeccionForm.LOCATARIO, SeccionForm.INMUEBLE, SeccionForm.PRECIO, SeccionForm.FECHA_FIRMA),
    ),
    ModeloContrato(
        "contrato_garante_propietario",
        "Garante propietario",
        secciones = setOf(SeccionForm.LOCADOR, SeccionForm.LOCATARIO, SeccionForm.GARANTIAS, SeccionForm.FECHA_FIRMA),
    ),
    ModeloContrato(
        "contrato_seguro_caucion",
        "Seguro caucion",
        secciones = setOf(SeccionForm.LOCADOR, SeccionForm.LOCATARIO, SeccionForm.GARANTIAS, SeccionForm.FECHA_FIRMA),
    ),
    ModeloContrato(
        "contrato_fiador_solidario",
        "Fiador solidario",
        secciones = setOf(SeccionForm.LOCADOR, SeccionForm.LOCATARIO, SeccionForm.GARANTIAS, SeccionForm.FECHA_FIRMA),
    ),
    ModeloContrato(
        "alquiler_ajuste_ipc",
        "Ajuste IPC",
        secciones = setOf(SeccionForm.LOCADOR, SeccionForm.LOCATARIO, SeccionForm.PRECIO, SeccionForm.FECHA_FIRMA),
    ),
    ModeloContrato(
        "alquiler_ajuste_semestral",
        "Ajuste semestral",
        secciones = setOf(SeccionForm.LOCADOR, SeccionForm.LOCATARIO, SeccionForm.PRECIO, SeccionForm.FECHA_FIRMA),
    ),
    ModeloContrato(
        "alquiler_precio_fijo",
        "Precio fijo",
        secciones = setOf(SeccionForm.LOCADOR, SeccionForm.LOCATARIO, SeccionForm.PRECIO, SeccionForm.FECHA_FIRMA),
    ),
    ModeloContrato(
        "alquiler_en_dolares",
        "Alquiler en dolares",
        monedaDolar = true,
        secciones = setOf(SeccionForm.LOCADOR, SeccionForm.LOCATARIO, SeccionForm.PRECIO, SeccionForm.FECHA_FIRMA),
    ),
    ModeloContrato(
        "notificacion_canon_actualizado",
        "Notificacion canon",
        secciones = setOf(SeccionForm.LOCADOR, SeccionForm.LOCATARIO, SeccionForm.PRECIO, SeccionForm.FECHA_FIRMA),
    ),
    ModeloContrato(
        "reparaciones_urgentes_locatario",
        "Reparaciones urgentes",
        labelLocador = "Remitente (Inquilino)",
        labelLocatario = "Destinatario (Locador)",
        secciones = setOf(SeccionForm.LOCADOR, SeccionForm.LOCATARIO, SeccionForm.INMUEBLE, SeccionForm.FECHA_FIRMA),
    ),
    ModeloContrato(
        "reparaciones_no_urgentes_locatario",
        "Reparaciones no urgentes",
        labelLocador = "Remitente (Inquilino)",
        labelLocatario = "Destinatario (Locador)",
        secciones = setOf(SeccionForm.LOCADOR, SeccionForm.LOCATARIO, SeccionForm.INMUEBLE, SeccionForm.FECHA_FIRMA),
    ),
    ModeloContrato(
        "recibo_de_alquiler",
        "Recibo alquiler",
        secciones = setOf(SeccionForm.LOCADOR, SeccionForm.LOCATARIO, SeccionForm.PRECIO, SeccionForm.FECHA_FIRMA),
    ),
    ModeloContrato(
        "acta_entrega_inmueble",
        "Acta de entrega",
        secciones = setOf(SeccionForm.LOCADOR, SeccionForm.LOCATARIO, SeccionForm.INMUEBLE, SeccionForm.FECHA_FIRMA),
    ),
    ModeloContrato("inventario_inicial", "Inventario inicial"),
    ModeloContrato("intimacion_de_pago", "Intimacion de pago"),
)

@OptIn(ExperimentalResourceApi::class)
@Composable
fun PantallaGeneradorDeContratoAlquiler(navController: androidx.navigation.NavController, clave: String?) {
    val modelo = remember(clave) {
        modelosContrato.firstOrNull { it.clave == clave }
            ?: ModeloContrato("contrato_locacion_familiar", "Generador de contrato")
    }

    var template by remember(modelo.clave) { mutableStateOf("") }
    var generatedText by remember(modelo.clave) { mutableStateOf("") }
    var error by remember(modelo.clave) { mutableStateOf<String?>(null) }

    var nombreLocador by remember { mutableStateOf("") }
    var dniLocador by remember { mutableStateOf("") }
    var domicilioLocador by remember { mutableStateOf("") }
    var mailLocador by remember { mutableStateOf("") }
    var telefonoLocador by remember { mutableStateOf("") }

    var nombreLocatario by remember { mutableStateOf("") }
    var dniLocatario by remember { mutableStateOf("") }
    var domicilioLocatario by remember { mutableStateOf("") }
    var mailLocatario by remember { mutableStateOf("") }
    var telefonoLocatario by remember { mutableStateOf("") }

    var direccionInmueble by remember { mutableStateOf("") }
    var cantidadAmbientes by remember { mutableStateOf("") }
    var plazoContrato by remember { mutableStateOf("") }
    var fechaInicio by remember { mutableStateOf<LocalDate?>(null) }
    var fechaFirma by remember { mutableStateOf<LocalDate?>(null) }
    var precioAlquiler by remember { mutableStateOf("") }
    var incluyeCochera by remember { mutableStateOf(false) }
    var cantidadCocheras by remember { mutableStateOf("1") }
    var mostrarPickerInicio by remember { mutableStateOf(false) }
    var mostrarPickerFirma by remember { mutableStateOf(false) }
    var monedaPesos by remember { mutableStateOf(!modelo.monedaDolar) }

    val tasasServicios = remember { mutableStateListOf(TasaServicio()) }
    val itemsInventario = remember { mutableStateListOf(ItemInventario()) }
    val garantesProp = remember { mutableStateListOf(GarantePropietario()) }
    val garantesRec = remember { mutableStateListOf(GaranteReciboSueldo()) }
    var caucion by remember { mutableStateOf(GarantiaCaucion()) }

    var expandInventario by remember { mutableStateOf(false) }
    var expandPropietaria by remember { mutableStateOf(false) }
    var expandRecibo by remember { mutableStateOf(false) }
    var expandCaucion by remember { mutableStateOf(false) }

    val thousandSeparator = ThousandSeparatorVisualTransformation()

    LaunchedEffect(modelo.clave) {
        generatedText = ""
        error = null
        plazoContrato = modelo.plazoDefault
        monedaPesos = !modelo.monedaDolar
        incluyeCochera = false
        cantidadCocheras = "1"
        expandInventario = false
        expandPropietaria = false
        expandRecibo = false
        expandCaucion = false
        tasasServicios.clear()
        tasasServicios.add(TasaServicio())
        itemsInventario.clear()
        itemsInventario.add(ItemInventario())
        garantesProp.clear()
        garantesProp.add(GarantePropietario())
        garantesRec.clear()
        garantesRec.add(GaranteReciboSueldo())
        caucion = GarantiaCaucion()
        template = try {
            extractTemplateForKey(modelo.clave)
        } catch (e: Exception) {
            error = "No se pudo cargar la plantilla base."
            ""
        }
    }

    val fechaFin = remember(fechaInicio, plazoContrato, modelo.plazoUnidad) {
        val inicio = fechaInicio ?: return@remember null
        val plazo = plazoContrato.toIntOrNull() ?: return@remember null
        if (plazo <= 0) return@remember null

        when (modelo.plazoUnidad) {
            PlazoUnidad.MESES -> inicio.plus(plazo, DateTimeUnit.MONTH).minus(1, DateTimeUnit.DAY)
            PlazoUnidad.DIAS -> inicio.plus(plazo, DateTimeUnit.DAY).minus(1, DateTimeUnit.DAY)
        }
    }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .background(MaterialTheme.colorScheme.background)
            .verticalScroll(rememberScrollState())
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(14.dp),
    ) {
        PremiumHeader(
            icono = Icons.Default.Description,
            titulo = modelo.titulo,
            subtitulo = "Generacion de contrato de locación vivienda",
        )

        if (error != null) {
            Card(
                modifier = Modifier.fillMaxWidth(),
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.errorContainer),
            ) {
                Text(
                    text = error.orEmpty(),
                    color = MaterialTheme.colorScheme.onErrorContainer,
                    modifier = Modifier.padding(14.dp),
                )
            }
        }

        if (modelo.secciones.contains(SeccionForm.LOCADOR)) {
            SectionLabelContrato("Datos del ${modelo.labelLocador}")
            FormFieldContrato("Nombre", nombreLocador) { nombreLocador = it }
            FormFieldContrato("DNI", dniLocador) { dniLocador = it.filter(Char::isDigit) }
            FormFieldContrato("Domicilio", domicilioLocador) { domicilioLocador = it }
            FormFieldContrato("Email", mailLocador) { mailLocador = it }
            FormFieldContrato("Telefono", telefonoLocador) { telefonoLocador = it }
        }

        if (modelo.secciones.contains(SeccionForm.LOCATARIO)) {
            SectionLabelContrato("Datos del ${modelo.labelLocatario}")
            FormFieldContrato("Nombre", nombreLocatario) { nombreLocatario = it }
            FormFieldContrato("DNI", dniLocatario) { dniLocatario = it.filter(Char::isDigit) }
            FormFieldContrato("Domicilio", domicilioLocatario) { domicilioLocatario = it }
            FormFieldContrato("Email", mailLocatario) { mailLocatario = it }
            FormFieldContrato("Telefono", telefonoLocatario) { telefonoLocatario = it }
        }

        if (modelo.secciones.contains(SeccionForm.INMUEBLE)) {
            SectionLabelContrato("Datos del inmueble")
            FormFieldContrato("Direccion del inmueble", direccionInmueble) { direccionInmueble = it }
            FormFieldContrato("Cantidad de ambientes", cantidadAmbientes) { cantidadAmbientes = it.filter(Char::isDigit) }
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween,
            ) {
                Text("Incluye cochera", style = MaterialTheme.typography.bodyLarge)
                FilterChip(
                    selected = incluyeCochera,
                    onClick = { incluyeCochera = !incluyeCochera },
                    label = { Text(if (incluyeCochera) "Si" else "No") }
                )
            }
            if (incluyeCochera) {
                FormFieldContrato("Cantidad de cocheras", cantidadCocheras) {
                    cantidadCocheras = it.filter(Char::isDigit)
                }
            }
        }

        if (modelo.secciones.contains(SeccionForm.CONDICIONES)) {
            SectionLabelContrato("Condiciones")
            FormFieldContrato(
                if (modelo.plazoUnidad == PlazoUnidad.DIAS) "Plazo (dias)" else "Plazo (meses)",
                plazoContrato
            ) { plazoContrato = it.filter(Char::isDigit) }
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                FechaBotonContrato(
                    label = "Fecha inicio",
                    value = fechaInicio?.formatDateUi(),
                    modifier = Modifier.weight(1f),
                    onClick = { mostrarPickerInicio = true }
                )
                if (modelo.secciones.contains(SeccionForm.FECHA_FIRMA)) {
                    FechaBotonContrato(
                        label = "Fecha firma",
                        value = fechaFirma?.formatDateUi(),
                        modifier = Modifier.weight(1f),
                        onClick = { mostrarPickerFirma = true }
                    )
                }
            }
            OutlinedTextField(
                value = fechaFin?.formatDateUi().orEmpty(),
                onValueChange = {},
                label = { Text("Fecha fin calculada") },
                readOnly = true,
                enabled = false,
                modifier = Modifier.fillMaxWidth(),
            )
        } else if (modelo.secciones.contains(SeccionForm.FECHA_FIRMA)) {
            SectionLabelContrato("Fecha de firma")
            FechaBotonContrato(
                label = "Fecha de firma",
                value = fechaFirma?.formatDateUi(),
                modifier = Modifier.fillMaxWidth(),
                onClick = { mostrarPickerFirma = true }
            )
        }

        if (modelo.secciones.contains(SeccionForm.PRECIO)) {
            SectionLabelContrato("Precio")
            Text("Moneda", style = MaterialTheme.typography.labelMedium, fontWeight = FontWeight.Bold)
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                FilterChip(selected = monedaPesos, onClick = { monedaPesos = true }, label = { Text("Pesos") })
                FilterChip(selected = !monedaPesos, onClick = { monedaPesos = false }, label = { Text("Dolares") })
            }
            FormFieldContrato(
                label = "Precio",
                value = precioAlquiler,
                keyboardType = KeyboardType.Number,
                visualTransformation = thousandSeparator,
                onValueChange = { precioAlquiler = it.filter { c -> c.isDigit() } }
            )
        }

        if (modelo.secciones.contains(SeccionForm.TASAS_SERVICIOS)) {
            SectionLabelContrato("Tasas y servicios")
            tasasServicios.forEachIndexed { index, tasa ->
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    OutlinedTextField(
                        value = tasa.tipo,
                        onValueChange = { tasasServicios[index] = tasa.copy(tipo = it) },
                        label = { Text("Tipo") },
                        singleLine = true,
                        modifier = Modifier.weight(1f),
                    )
                    OutlinedTextField(
                        value = tasa.numeroCuenta,
                        onValueChange = { tasasServicios[index] = tasa.copy(numeroCuenta = it) },
                        label = { Text("Nro cuenta") },
                        singleLine = true,
                        modifier = Modifier.width(130.dp),
                    )
                    if (tasasServicios.size > 1) {
                        IconButton(onClick = { tasasServicios.removeAt(index) }) {
                            Icon(Icons.Default.Delete, contentDescription = "Eliminar")
                        }
                    }
                }
            }
            OutlinedButton(
                onClick = { tasasServicios.add(TasaServicio()) },
                modifier = Modifier.fillMaxWidth(),
            ) {
                Icon(Icons.Default.Add, contentDescription = null)
                Spacer(modifier = Modifier.width(8.dp))
                Text("Agregar tasa o servicio")
            }
        }

        if (modelo.secciones.contains(SeccionForm.INVENTARIO) && !modelo.esTemporario) {
            SectionLabelContrato("Inventario")
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween,
            ) {
                Text("Cargar inventario detallado", style = MaterialTheme.typography.bodyLarge)
                FilterChip(
                    selected = expandInventario,
                    onClick = { expandInventario = !expandInventario },
                    label = { Text(if (expandInventario) "Activo" else "Inactivo") }
                )
            }
            if (expandInventario) {
                itemsInventario.forEachIndexed { index, item ->
                    Card(
                        modifier = Modifier.fillMaxWidth(),
                        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.4f)),
                    ) {
                        Column(modifier = Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically,
                            ) {
                                Text("Item ${index + 1}", fontWeight = FontWeight.SemiBold)
                                if (itemsInventario.size > 1) {
                                    IconButton(onClick = { itemsInventario.removeAt(index) }) {
                                        Icon(Icons.Default.Delete, contentDescription = "Eliminar item")
                                    }
                                }
                            }
                            FormFieldContrato("Descripcion", item.descripcion) {
                                itemsInventario[index] = item.copy(descripcion = it)
                            }
                            FormFieldContrato("Cantidad", item.cantidad) {
                                itemsInventario[index] = item.copy(cantidad = it.filter(Char::isDigit))
                            }
                            FormFieldContrato("Detalles", item.detalles) {
                                itemsInventario[index] = item.copy(detalles = it)
                            }
                            Text("Categoria", style = MaterialTheme.typography.labelMedium)
                            Row(horizontalArrangement = Arrangement.spacedBy(6.dp), modifier = Modifier.fillMaxWidth()) {
                                CategoriaInventario.entries.forEach { categoria ->
                                    FilterChip(
                                        selected = item.categoria == categoria,
                                        onClick = { itemsInventario[index] = item.copy(categoria = categoria) },
                                        label = { Text(categoria.label) }
                                    )
                                }
                            }
                        }
                    }
                }
                OutlinedButton(
                    onClick = { itemsInventario.add(ItemInventario()) },
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    Icon(Icons.Default.Add, contentDescription = null)
                    Spacer(modifier = Modifier.width(8.dp))
                    Text("Agregar item")
                }
            }
        }

        if (modelo.secciones.contains(SeccionForm.GARANTIAS)) {
            SectionLabelContrato("Garantias")
            ExpandSectionToggle("Garantia propietaria", expandPropietaria) { expandPropietaria = !expandPropietaria }
            if (expandPropietaria) {
                garantesProp.forEachIndexed { index, garante ->
                    Card(modifier = Modifier.fillMaxWidth()) {
                        Column(modifier = Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                                Text("Garante ${index + 1}", fontWeight = FontWeight.SemiBold)
                                if (garantesProp.size > 1) {
                                    IconButton(onClick = { garantesProp.removeAt(index) }) {
                                        Icon(Icons.Default.Delete, contentDescription = "Eliminar garante")
                                    }
                                }
                            }
                            FormFieldContrato("Nombre", garante.nombre) { garantesProp[index] = garante.copy(nombre = it) }
                            FormFieldContrato("DNI", garante.dni) { garantesProp[index] = garante.copy(dni = it.filter(Char::isDigit)) }
                            FormFieldContrato("Domicilio", garante.domicilio) { garantesProp[index] = garante.copy(domicilio = it) }
                            FormFieldContrato("Email", garante.email) { garantesProp[index] = garante.copy(email = it) }
                            FormFieldContrato("Telefono", garante.telefono) { garantesProp[index] = garante.copy(telefono = it) }
                            FormFieldContrato("Direccion del inmueble en garantia", garante.direccionInmueble) { garantesProp[index] = garante.copy(direccionInmueble = it) }
                            FormFieldContrato("Datos del inmueble", garante.datosInmueble) { garantesProp[index] = garante.copy(datosInmueble = it) }
                        }
                    }
                }
                OutlinedButton(onClick = { garantesProp.add(GarantePropietario()) }, modifier = Modifier.fillMaxWidth()) {
                    Icon(Icons.Default.Add, contentDescription = null)
                    Spacer(modifier = Modifier.width(8.dp))
                    Text("Agregar garante propietario")
                }
            }

            ExpandSectionToggle("Recibo de sueldo", expandRecibo) { expandRecibo = !expandRecibo }
            if (expandRecibo) {
                garantesRec.forEachIndexed { index, garante ->
                    Card(modifier = Modifier.fillMaxWidth()) {
                        Column(modifier = Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                                Text("Garante ${index + 1}", fontWeight = FontWeight.SemiBold)
                                if (garantesRec.size > 1) {
                                    IconButton(onClick = { garantesRec.removeAt(index) }) {
                                        Icon(Icons.Default.Delete, contentDescription = "Eliminar garante")
                                    }
                                }
                            }
                            FormFieldContrato("Nombre", garante.nombre) { garantesRec[index] = garante.copy(nombre = it) }
                            FormFieldContrato("DNI", garante.dni) { garantesRec[index] = garante.copy(dni = it.filter(Char::isDigit)) }
                            FormFieldContrato("Domicilio", garante.domicilio) { garantesRec[index] = garante.copy(domicilio = it) }
                            FormFieldContrato("Email", garante.email) { garantesRec[index] = garante.copy(email = it) }
                            FormFieldContrato("Telefono", garante.telefono) { garantesRec[index] = garante.copy(telefono = it) }
                            FormFieldContrato("Empleador", garante.empleador) { garantesRec[index] = garante.copy(empleador = it) }
                            FormFieldContrato("Domicilio empleador", garante.domicilioEmpleador) { garantesRec[index] = garante.copy(domicilioEmpleador = it) }
                            FormFieldContrato("Email empleador", garante.emailEmpleador) { garantesRec[index] = garante.copy(emailEmpleador = it) }
                            FormFieldContrato("Telefono empleador", garante.telefonoEmpleador) { garantesRec[index] = garante.copy(telefonoEmpleador = it) }
                            FormFieldContrato("Monto recibo", garante.montoRecibo) { garantesRec[index] = garante.copy(montoRecibo = it.filter(Char::isDigit)) }
                            FormFieldContrato("Nombre fiador", garante.nombreFiador) { garantesRec[index] = garante.copy(nombreFiador = it) }
                            FormFieldContrato("DNI fiador", garante.dniFiador) { garantesRec[index] = garante.copy(dniFiador = it.filter(Char::isDigit)) }
                            FormFieldContrato("Domicilio fiador", garante.domicilioFiador) { garantesRec[index] = garante.copy(domicilioFiador = it) }
                            FormFieldContrato("Email fiador", garante.emailFiador) { garantesRec[index] = garante.copy(emailFiador = it) }
                            FormFieldContrato("Telefono fiador", garante.telefonoFiador) { garantesRec[index] = garante.copy(telefonoFiador = it) }
                        }
                    }
                }
                OutlinedButton(onClick = { garantesRec.add(GaranteReciboSueldo()) }, modifier = Modifier.fillMaxWidth()) {
                    Icon(Icons.Default.Add, contentDescription = null)
                    Spacer(modifier = Modifier.width(8.dp))
                    Text("Agregar garante por recibo")
                }
            }

            ExpandSectionToggle("Seguro de caucion", expandCaucion) { expandCaucion = !expandCaucion }
            if (expandCaucion) {
                Card(modifier = Modifier.fillMaxWidth()) {
                    Column(modifier = Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        FormFieldContrato("Aseguradora", caucion.aseguradora) { caucion = caucion.copy(aseguradora = it) }
                        FormFieldContrato("Numero de poliza", caucion.numeroPoliza) { caucion = caucion.copy(numeroPoliza = it) }
                        FormFieldContrato("Domicilio", caucion.domicilio) { caucion = caucion.copy(domicilio = it) }
                        FormFieldContrato("Email", caucion.email) { caucion = caucion.copy(email = it) }
                        FormFieldContrato("Telefono", caucion.telefono) { caucion = caucion.copy(telefono = it) }
                    }
                }
            }
        }

        val userStatus by com.rosario.juridico.domain.AppUsageManager.instance.userStatus.collectAsState()
        val accessState = com.rosario.juridico.domain.AppUsageManager.instance.checkAccess()
        val isPremium = accessState == com.rosario.juridico.domain.AccessState.PREMIUM_ACTIVE
        
        val quota = com.rosario.juridico.platform.UserQuota(
            initial_uses_remaining = userStatus.initial_uses_remaining,
            last_monthly_use_timestamp = userStatus.last_monthly_use_timestamp
        )
        val accessResult = com.rosario.juridico.platform.checkAccess(quota)
        val isLocked = !isPremium && accessResult == com.rosario.juridico.platform.AccessResult.DENIED

        CalculatorActionLock(
            navController = navController,
            onAction = {
                if (template.isBlank()) {
                    error = "La plantilla aun no esta disponible."
                } else {
                    error = null
                    generatedText = generateContractText(
                        template = template,
                        nombreLocador = nombreLocador,
                        dniLocador = dniLocador,
                        domicilioLocador = domicilioLocador,
                        mailLocador = mailLocador,
                        telefonoLocador = telefonoLocador,
                        nombreLocatario = nombreLocatario,
                        dniLocatario = dniLocatario,
                        domicilioLocatario = domicilioLocatario,
                        mailLocatario = mailLocatario,
                        telefonoLocatario = telefonoLocatario,
                        direccionInmueble = direccionInmueble,
                        cantidadAmbientes = cantidadAmbientes,
                        plazoContrato = plazoContrato,
                        fechaInicio = fechaInicio,
                        fechaFin = fechaFin,
                        fechaFirma = fechaFirma,
                        precioAlquiler = precioAlquiler,
                        tasasServicios = buildTasasServiciosText(tasasServicios),
                        inventario = buildInventarioText(expandInventario, itemsInventario),
                        tipoGarantia = buildGarantiasText(
                            expandPropietaria = expandPropietaria,
                            garantesProp = garantesProp,
                            expandRecibo = expandRecibo,
                            garantesRec = garantesRec,
                            expandCaucion = expandCaucion,
                            caucion = caucion,
                        ),
                        incluyeCochera = incluyeCochera,
                        cantidadCocheras = cantidadCocheras,
                        monedaPesos = monedaPesos,
                    )
                }
            }
        ) { onClick ->
            Button(
                onClick = onClick,
                modifier = Modifier.fillMaxWidth(),
            ) {
                if (isLocked) {
                    Icon(Icons.Default.Lock, null, modifier = Modifier.size(18.dp))
                    Spacer(Modifier.width(8.dp))
                }
                Text("Generar borrador")
            }
        }

        if (generatedText.isNotBlank()) {
            HorizontalDivider()
            val textoCopia = """
                ${modelo.titulo.uppercase()} - BORRADOR GENERADO
                -------------------------------------------
                $generatedText
                -------------------------------------------
                Generado con www.juridicorosario.com
            """.trimIndent()

            CajaResultadoAccionable(
                titulo = "Vista Previa del Borrador",
                textoParaCompartir = textoCopia,
                colorFondo = MaterialTheme.colorScheme.surface
            ) {
                TextoInteractivoAI(generatedText)
            }
        }

        Spacer(modifier = Modifier.height(36.dp))
    }

    if (mostrarPickerInicio) {
        JuridicoDatePickerDialogKMP(
            onDateSelected = { fechaInicio = it },
            onDismiss = { mostrarPickerInicio = false },
        )
    }
    if (mostrarPickerFirma) {
        JuridicoDatePickerDialogKMP(
            onDateSelected = { fechaFirma = it },
            onDismiss = { mostrarPickerFirma = false },
        )
    }
}

@Composable
private fun SectionLabelContrato(texto: String) {
    Text(
        text = texto,
        style = MaterialTheme.typography.titleSmall,
        fontWeight = FontWeight.Bold,
        color = MaterialTheme.colorScheme.primary,
    )
}

@Composable
private fun FormFieldContrato(
    label: String,
    value: String,
    minLines: Int = 1,
    keyboardType: KeyboardType = KeyboardType.Text,
    visualTransformation: androidx.compose.ui.text.input.VisualTransformation = androidx.compose.ui.text.input.VisualTransformation.None,
    onValueChange: (String) -> Unit,
) {
    OutlinedTextField(
        value = value,
        onValueChange = onValueChange,
        label = { Text(label) },
        modifier = Modifier.fillMaxWidth(),
        minLines = minLines,
        keyboardOptions = KeyboardOptions(keyboardType = keyboardType),
        visualTransformation = visualTransformation,
    )
}

@Composable
private fun ExpandSectionToggle(
    label: String,
    selected: Boolean,
    onClick: () -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(label, style = MaterialTheme.typography.bodyLarge)
        FilterChip(
            selected = selected,
            onClick = onClick,
            label = { Text(if (selected) "Activo" else "Inactivo") }
        )
    }
}

@Composable
private fun FechaBotonContrato(
    label: String,
    value: String?,
    modifier: Modifier = Modifier,
    onClick: () -> Unit,
) {
    Button(
        onClick = onClick,
        modifier = modifier.height(52.dp),
        contentPadding = PaddingValues(horizontal = 8.dp)
    ) {
        Icon(
            imageVector = Icons.Default.DateRange,
            contentDescription = null,
            modifier = Modifier.size(20.dp)
        )
        Spacer(modifier = Modifier.width(6.dp))
        Text(
            text = value ?: label,
            style = MaterialTheme.typography.labelMedium,
            maxLines = 1
        )
    }
}

@OptIn(ExperimentalResourceApi::class)
private suspend fun extractTemplateForKey(key: String): String = withContext(Dispatchers.Default) {
    try {
        val fullText = Res.readBytes("files/modelo_escritos_alquiler.txt").decodeToString()
        val startMarker = "[$key]"
        val startIndex = fullText.indexOf(startMarker)
        if (startIndex == -1) {
            return@withContext Res.readBytes("files/contrato_alquiler_base.txt").decodeToString()
        }
        val contentStart = startIndex + startMarker.length
        // Buscamos el inicio de la proxima seccion (un [ al principio de una linea)
        // para evitar detenernos en los placeholders como [Nombre del LOCADOR]
        val nextTagIndex = fullText.indexOf("\n[", contentStart)
        if (nextTagIndex != -1) {
            fullText.substring(contentStart, nextTagIndex).trim()
        } else {
            fullText.substring(contentStart).trim()
        }
    } catch (e: Exception) {
        ""
    }
}

private fun buildTasasServiciosText(
    tasasServicios: List<TasaServicio>,
): String {
    val text = buildString {
        tasasServicios
            .filter { it.tipo.isNotBlank() }
            .forEach { tasa ->
                val cuenta = if (tasa.numeroCuenta.isNotBlank()) ", Nro de cuenta: ${tasa.numeroCuenta}" else ""
                appendLine("- ${tasa.tipo}$cuenta")
            }
    }.trim()

    return if (text.isBlank()) "No especificadas." else text
}

private fun buildInventarioText(
    expandInventario: Boolean,
    itemsInventario: List<ItemInventario>,
): String {
    if (!expandInventario) return "Sin inventario especificado."
    val activos = itemsInventario.filter { it.descripcion.isNotBlank() }
    if (activos.isEmpty()) return "Sin inventario especificado."

    return buildString {
        appendLine("INVENTARIO DEL INMUEBLE")
        appendLine()
        activos.forEachIndexed { index, item ->
            val detalle = if (item.detalles.isNotBlank()) " - ${item.detalles}" else ""
            appendLine("${index + 1}. ${item.cantidad}x ${item.descripcion} (${item.categoria.label})$detalle")
        }
        appendLine()
        append("Todos los bienes detallados se entregan en buen estado de conservacion y funcionamiento.")
    }.trim()
}

private fun buildGarantiasText(
    expandPropietaria: Boolean,
    garantesProp: List<GarantePropietario>,
    expandRecibo: Boolean,
    garantesRec: List<GaranteReciboSueldo>,
    expandCaucion: Boolean,
    caucion: GarantiaCaucion,
): String {
    return buildString {
        if (expandPropietaria) {
            appendLine("GARANTIA PROPIETARIA:")
            garantesProp.forEachIndexed { index, g ->
                appendLine("  Garante ${index + 1}: ${g.nombre}, DNI ${g.dni}, domicilio: ${g.domicilio}, email: ${g.email}, tel: ${g.telefono}")
                appendLine("  Inmueble: ${g.direccionInmueble} - ${g.datosInmueble}")
            }
        }
        if (expandRecibo) {
            appendLine("GARANTIA RECIBO DE SUELDO:")
            garantesRec.forEachIndexed { index, g ->
                appendLine("  Garante ${index + 1}: ${g.nombre}, DNI ${g.dni}, domicilio: ${g.domicilio}, email: ${g.email}, tel: ${g.telefono}")
                appendLine("  Empleador: ${g.empleador}, domicilio: ${g.domicilioEmpleador}, email: ${g.emailEmpleador}, tel: ${g.telefonoEmpleador}, recibo: $${g.montoRecibo}")
                appendLine("  Fiador: ${g.nombreFiador}, DNI: ${g.dniFiador}, domicilio: ${g.domicilioFiador}, email: ${g.emailFiador}, tel: ${g.telefonoFiador}")
            }
        }
        if (expandCaucion) {
            appendLine("SEGURO DE CAUCION:")
            appendLine("  Aseguradora: ${caucion.aseguradora}, Poliza Nro: ${caucion.numeroPoliza}")
            appendLine("  Domicilio: ${caucion.domicilio}, email: ${caucion.email}, tel: ${caucion.telefono}")
        }
    }.trim().ifBlank { "Sin garantia especificada" }
}

private fun generateContractText(
    template: String,
    nombreLocador: String,
    dniLocador: String,
    domicilioLocador: String,
    mailLocador: String,
    telefonoLocador: String,
    nombreLocatario: String,
    dniLocatario: String,
    domicilioLocatario: String,
    mailLocatario: String,
    telefonoLocatario: String,
    direccionInmueble: String,
    cantidadAmbientes: String,
    plazoContrato: String,
    fechaInicio: LocalDate?,
    fechaFin: LocalDate?,
    fechaFirma: LocalDate?,
    precioAlquiler: String,
    tasasServicios: String,
    inventario: String,
    tipoGarantia: String,
    incluyeCochera: Boolean,
    cantidadCocheras: String,
    monedaPesos: Boolean,
): String {
    var result = template
    val placeholders = linkedMapOf(
        "[Nombre del LOCADOR]" to nombreLocador.ifBlank { "________________" },
        "[DNI del LOCADOR]" to dniLocador.ifBlank { "________________" },
        "[Domicilio del LOCADOR]" to domicilioLocador.ifBlank { "________________" },
        "[Mail del LOCADOR]" to mailLocador.ifBlank { "________________" },
        "[Teléfono del LOCADOR]" to telefonoLocador.ifBlank { "________________" },
        "[Nombre del LOCATARIO]" to nombreLocatario.ifBlank { "________________" },
        "[DNI del LOCATARIO]" to dniLocatario.ifBlank { "________________" },
        "[Domicilio del LOCATARIO]" to domicilioLocatario.ifBlank { "________________" },
        "[Mail del LOCATARIO]" to mailLocatario.ifBlank { "________________" },
        "[Teléfono del LOCATARIO]" to telefonoLocatario.ifBlank { "________________" },
        "[Dirección del Inmueble]" to direccionInmueble.ifBlank { "________________" },
        "[Cantidad de ambientes]" to cantidadAmbientes.ifBlank { "________________" },
        "[Plazo del Contrato]" to plazoContrato.ifBlank { "________________" },
        "[Fecha de inicio]" to (fechaInicio?.formatDateUi() ?: "________________"),
        "[Fecha de finalización]" to (fechaFin?.formatDateUi() ?: "________________"),
        "[Precio del Alquiler]" to buildPrice(monedaPesos, precioAlquiler),
        "[Tasas y servicios a cargo del inquilino]" to tasasServicios,
        "[Inventario del Inmueble]" to inventario,
        "[opción: garantía propietaria / seguro de caución / recibo de sueldo de terceros, etc.]" to tipoGarantia,
    )

    placeholders.forEach { (placeholder, value) ->
        result = result.replace(placeholder, value, ignoreCase = true)
    }

    result = result.replace(
        "[cochera]",
        if (incluyeCochera) {
            ", con ${cantidadCocheras.ifBlank { "1" }} cochera${if (cantidadCocheras != "1") "s" else ""}"
        } else {
            ""
        },
        ignoreCase = true
    )

    if (fechaFirma != null) {
        result = result
            .replace("[día]", fechaFirma.dayOfMonth.toString(), ignoreCase = true)
            .replace("[mes]", fechaFirma.month.name.lowercase().replaceFirstChar { it.uppercase() }, ignoreCase = true)
            .replace("[año]", fechaFirma.year.toString(), ignoreCase = true)
    }

    return result
}

private fun buildPrice(monedaPesos: Boolean, precio: String): String {
    val clean = precio.ifBlank { "________________" }
    if (clean == "________________") return clean
    
    val formatted = clean.toDoubleOrNull()?.toFormato(0, true) ?: clean
    return if (monedaPesos) "$ $formatted" else "U\$S $formatted"
}

private fun LocalDate.formatDateUi(): String {
    val day = dayOfMonth.toString().padStart(2, '0')
    val monthValue = monthNumber.toString().padStart(2, '0')
    return "$day/$monthValue/$year"
}
